import React, {useEffect, useState} from "react";
import {Template} from "../../../../components/template/template.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {Card} from "../../../../components/card/card.jsx";
import Button from "../../../../components/button/button.jsx";
import {useParams} from "react-router";
import {FaPlus} from "react-icons/fa";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import SuppliesService from "../../../../services/web/supplies-service.js";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {useQuery} from "@tanstack/react-query";
import InputDatetime from "../../../../components/input/input-datetime.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import {formatStoredFuelQuantity, formatStoredMoneyValue, MaskDefault} from "../../../../utils/mask.js";
import projectUtils from "../../../../utils/project-utils.js";
import userService from "../../../../services/web/user-service.js";
import Attach from "../../../../components/attach/attach.jsx";
import {FaCircleExclamation, FaTriangleExclamation} from "react-icons/fa6";
import responsibleMaintenanceService from "../../../../services/web/responsible-maintenance-service.js";
import suppliesService from "../../../../services/web/supplies-service.js";

const initialState = {
    vehicles_id: "",
    conductor: "",
    gas_stations_id: "",
    authorization_code: "",
    date: null,
    fuel_type: "",
    value_pay: "",
    fuel_quant: "",
    current_km: "",
    full_tank: false,
    receipt_file: null,
    receipt_file_attach: null,
    receipt_removed: false,
    hasPermission: false
};

const fuelOptions = [
    { value: "arla32", label: "Arla 32 (litros)" },
    { value: "diesel", label: "Diesel (litros)" },
    { value: "gasoline", label: "Gasolina (litros)" },
    { value: "ethanol", label: "Etanol (litros)" },
    { value: "gnv", label: "GNV (m³)" },
    { value: "electric", label: "Elétrico (kWh)" }
];

const SuppliesForm = () => {
    const { id } = useParams();
    const navigate = navigateHook();

    const [state, setState] = useState(initialState);
    const [cnhUi, setCnhUi] = useState({ status: null, text: "", icon: null });

    const { isLoading, data } = useQuery({
        queryKey: ["supply", id],
        enabled:!!id,
        queryFn: () => SuppliesService.getById(id).then(({ data }) => data)
    });

    useEffect(() => {
        if (!!id && data) {
            const gasStation = JSON.parse(data.gas_station) || {};

            setState({
                vehicles_id: data.vehicles_id,
                conductor: data.conductor,
                gas_stations_id: gasStation?.id,
                date: data.date,
                fuel_type: data.fuel_type,
                value_pay: formatStoredMoneyValue(data.value_pay),
                fuel_quant: formatStoredFuelQuantity(data.fuel_quant),
                current_km: data.current_km,
                full_tank: data.full_tank,
                receipt_file: null,
                receipt_file_attach: data.receipt_file ?? null,
                receipt_removed: false,
                hasPermission: data.hasPermission,
                updated_user: data.updated_user,
                updated_user_name: data.updated_user_name
            });
        } else  {
            setState(initialState);
        }
    }, [id, data]);

    useEffect(() => {
        let isMounted = true;

        async function fetchCnhStatus() {
            const defaultState = { status: null, text: "", icon: null };

            if (!state?.conductor) {
                if (isMounted) setCnhUi(defaultState);
                return;
            }

            try {
                const cnhStatus = await userService.conductorCnhStatus(state.conductor);

                if (cnhStatus === "missing") {
                    if (isMounted) setCnhUi({
                        status: "danger",
                        text: "Não possui CNH",
                        icon: <FaTriangleExclamation className="text-red-500" />
                    });
                    return;
                }

                if (cnhStatus === "unknown") {
                    if (isMounted) setCnhUi(defaultState);
                    return;
                }

                if (cnhStatus === "expired") {
                    if (isMounted) setCnhUi({
                        status: "danger",
                        text: "CNH vencida",
                        icon: <FaTriangleExclamation className="text-red-500" />,
                    });
                    return;
                }

                if (cnhStatus === "expiring") {
                    if (isMounted) setCnhUi({
                        status: "warning",
                        text: "CNH a vencer",
                        icon: <FaCircleExclamation className="text-amber-500" />,
                    });
                    return;
                }

                if (isMounted && cnhStatus === "valid") setCnhUi({
                    status: "success",
                    text: "CNH Válida",
                    icon: null
                });

            } catch (e) {
                console.error("Erro ao buscar status da CNH:", e);
                if (isMounted) setCnhUi(defaultState);
            }
        }

        fetchCnhStatus();

        return () => {
            isMounted = false;
        };
    }, [state?.conductor]);


    const { data: conductors } = useQuery({
        queryKey: ['responsible'],
        queryFn: () => userService.optionsWithoutSystem(),
    });

    const { data: vehicles } = useQuery({
        queryKey: ['vehicles'],
        queryFn: () => SuppliesService.vehicles(),
    });

    const { data: gasStations = [] } = useQuery({
        queryFn: async () => {
            const response = await SuppliesService.gasStations(
                state.gas_stations_id || null,
                id || null
            );

            const list = response?.data ?? response ?? [];

            return list.map(item => ({
                value: item.id,
                label: item.text
            }));
        },
        queryKey: ["gasStations", state.gas_stations_id, id]
    });

    const handleChange = (value, field) => {
        setState(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleVehicleChange = (select) => {
        setState(prev => ({
            ...prev,
            vehicles_id: select?.value || null,
            current_km: select?.actual_km ?? prev.current_km
        }));
    };

    const handleAttachChange = (file) => {
        if (!(file instanceof File)) return;

        setState(prev => ({
            ...prev,
            receipt_file: file,
            receipt_removed: false
        }));
    };

    const handleRemoveAttach = () => {
        setState(prev => ({
            ...prev,
            receipt_file: null,
            receipt_removed: true,
            receipt_file_attach: null
        }));
    };

    const handleSave = async () => {
        try {
            const formData = new FormData();

            formData.append("date", projectUtils.formatDate(state.date, "DD/MM/YYYY HH:mm"));
            formData.append("vehicles_id", state.vehicles_id ?? "");
            formData.append("conductor", state.conductor ?? "");
            formData.append("gas_stations_id", state.gas_stations_id ?? "");
            formData.append("authorization_code", state.authorization_code?.key ?? state.authorization_code ?? "");
            formData.append("fuel_type", state.fuel_type ?? "");

            const valuePay = String(state.value_pay ?? "").replace(/\D/g, "");

            const fuelQuant = String(state.fuel_quant ?? "")
                .replace(/\./g, "")
                .replace(",", ".");

            formData.append("value_pay", valuePay);
            formData.append("fuel_quant", fuelQuant);

            formData.append("current_km", state.current_km ?? "");

            if (state.full_tank) formData.append("full_tank", "on");

            const hasNewReceiptFile = state.receipt_file instanceof File;
            if (hasNewReceiptFile) formData.append("receipt_file", state.receipt_file);

            if (state.receipt_removed) {
                formData.append("receipt_file_attach", "");
            } else if (!hasNewReceiptFile && state.receipt_file_attach) {
                formData.append("receipt_file", state.receipt_file_attach);
                formData.append("receipt_file_attach", state.receipt_file_attach);
            }

            let response;
            if (id) {
                formData.append('_method', 'PUT');
                response = await SuppliesService.update(id, formData);
            }

            else response = await SuppliesService.create(formData);
            const savedId = response?.data?.id ?? response?.id;

            projectUtils.success(!id);
            navigate(`/supplies/form/${savedId}`, false);
        } catch (error) {
            console.error(error);

            let message = "Não foi possível salvar.";
            if (error?.response?.status !== 500) message = error?.response?.data?.message ?? message;
            projectUtils.swalErrorMessage("", message, "Ok");
        }
    };

    const handleDelete = async () => {
        projectUtils.swalMessage(
            "warning",
            "Excluir",
            `Excluir o abastecimento "${id}"?`,
            "Sim",
            true,
            "Não",
            ({isConfirmed}) => {
                if (isConfirmed) {
                    suppliesService.delete(id)
                        .then(() => {
                            projectUtils.success(true, "Excluído com sucesso");
                            navigate("/supplies");
                        })
                        .catch((error) => {
                            console.error(error);
                            let message = 'Não foi possível salvar.';
                            if (error.response.status !== 500) {
                                message = error.response.data.message;
                            }

                            projectUtils.swalErrorMessage('', message, 'Ok');
                        })
                }
            }
        );
    };

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Abastecimento</Template.Title>

                { id && (
                    <Button
                        target="primary-outline"
                        onClick={() => navigate("/supplies/form", false)}
                    >
                        <FaPlus /> Adicionar abastecimento
                    </Button>
                )}
            </Template.Header>

            <Template.Body>
                <Card>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-4">
                            <InputDatetime
                                id="dtStart"
                                label="Data/hora *"
                                selected={state.date}
                                onChange={(v) => handleChange(v, "date")}
                                disabled={id && !state.hasPermission}
                                classNameWrapper="col-span-2"
                            />

                            <SelectComponent
                                id={"vehicle_id"}
                                label="Veículo *"
                                options={vehicles}
                                value={state.vehicles_id}
                                onChange={handleVehicleChange}
                                isDisabled={id && !state.hasPermission}
                                classNameWrapper={state.authorization_code?.value === 'true' ? "col-span-3" : "col-span-4"}
                            />

                            <SelectComponent
                                label="Condutor *"
                                options={conductors}
                                value={state.conductor}
                                onChange={(opt) => handleChange(opt?.value, "conductor")}
                                status={cnhUi.status}
                                helperText={cnhUi.text}
                                customIcon={cnhUi.icon}
                                isDisabled={id && !state.hasPermission}
                                classNameWrapper={state.authorization_code?.value === 'true' ? "col-span-2" : "col-span-3"}
                            />

                            <SelectComponent
                                label="Posto *"
                                options={gasStations}
                                value={state.gas_stations_id}
                                onChange={(opt) => handleChange(opt?.value, "gas_stations_id")}
                                isDisabled={id && !state.hasPermission}
                                classNameWrapper={state.authorization_code?.value === 'true' ? "col-span-2" : "col-span-3"}
                            />

                            { state.authorization_code?.value === 'true' &&
                                <InputForm
                                    label="Autorização"
                                    value={state.authorization_code?.key}
                                    onChange={(e) => handleChange(e.target.value, "authorization_code")}
                                    disabled={id && !state.hasPermission}
                                    classNameWrapper="col-span-2"
                                />
                            }

                            <SelectComponent
                                label="Tipo combustível *"
                                options={fuelOptions}
                                value={fuelOptions?.find(f => f.value === state.fuel_type) || null}
                                onChange={(opt) => handleChange(opt?.value ?? "", "fuel_type")}
                                isDisabled={id && !state.hasPermission}
                                classNameWrapper="col-span-2"
                            />

                            <InputForm
                                id={"value_pay"}
                                label="Valor pago *"
                                value={state.value_pay}
                                onChange={(e) => handleChange(e.target.value, "value_pay")}
                                mask={MaskDefault.money}
                                disabled={id && !state.hasPermission}
                                classNameWrapper="col-span-2"
                            />

                            <InputForm
                                id={"fuel_quant"}
                                label="Quant. combustível *"
                                value={state.fuel_quant}
                                onChange={(e) => handleChange(e.target.value, "fuel_quant")}
                                mask={MaskDefault.fuelQuantity}
                                maskOptions={{
                                    scale: 3,
                                    radix: ",",
                                    thousandsSeparator: ".",
                                    normalizeZeros: true,
                                    padFractionalZeros: false
                                }}
                                disabled={id && !state.hasPermission}
                                classNameWrapper="col-span-2"
                            />

                            <InputForm
                                id={"current_km"}
                                label="Km ao abastecer *"
                                integerOnly
                                type="number"
                                value={state.current_km}
                                onChange={(e) => handleChange(e.target.value, "current_km")}
                                disabled={id && !state.hasPermission}
                                classNameWrapper="col-span-2"
                            />

                            <div className="col-span-2 flex items-center justify-center">
                                <InputCheckbox
                                    id={"full_tank"}
                                    label="Completou tanque?"
                                    checked={state.full_tank}
                                    onChange={(e) => handleChange(e.target.checked, "full_tank")}
                                    disabled={id && !state.hasPermission}
                                />
                            </div>

                            <div className="col-span-2">
                                <Attach
                                    onChangeFile={handleAttachChange}
                                    removeFile={handleRemoveAttach}
                                    itemId="receipt_file"
                                    attachId={state.receipt_file_attach}
                                    attachedButtonDisabled={!!id && !state.hasPermission}
                                    showRemove={!id || state.hasPermission}
                                />
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card className="mt-2">
                    <Card.Body className={"flex justify-between"}>
                        <div className={"flex justify-center items-center"}>
                            { state.hasPermission && state.updated_user && state.updated_at &&
                                <span className={"text-sm"}>Editado por <b>{state.updated_user} - {state.updated_user_name}</b> em <b>{projectUtils.formatDate(state.updated_at)}</b></span>
                            }
                        </div>
                        <div className="flex justify-end gap-2">
                            { id && (
                                <Button target="danger" onClick={handleDelete} actionFeedbackType="delete">
                                    Excluir
                                </Button>
                            )}
                            <Button onClick={handleSave} disabled={id && !state.hasPermission} actionFeedbackType="save">
                                Salvar
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default SuppliesForm;
